import axios from 'axios';
import authService from './authService';

// Base API configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 3000
});

// Request interceptor - adds auth token to every request
apiClient.interceptors.request.use(
  (config) => {
    // Determine user type based on URL
    const isAdminEndpoint = config.url.includes('/admin');
    const userType = isAdminEndpoint ? 'admin' : 'brand';
    
    const token = authService.getToken(userType);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      authService.refreshTokenExpiry(userType);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isAdminEndpoint = error.config.url.includes('/admin');
      
      // ✅ FIX: Check current path to determine redirect
      const currentPath = window.location.pathname;
      const isAdminPage = currentPath.includes('/admin');
      
      const userType = isAdminPage ? 'admin' : 'brand';
      const loginPath = isAdminPage ? '/admin-login' : '/';
      
      authService.logout(userType);
      
      if (!currentPath.includes(loginPath)) {
        window.location.href = `${loginPath}?returnUrl=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

// API service functions
const apiService = {
  // Authentication
  adminLogin: (credentials) => authService.adminLogin(credentials),
  brandLogin: (credentials) => authService.brandLogin(credentials),
  
  // Profile
  getProfile: (userType = 'brand') => apiClient.get(userType === 'admin' ? '/admin/profile' : '/brand/profile'),
  
  // ✅ FIX: Admin Brand Management - use correct endpoints
  createBrand: (brandData) => apiClient.post('/admin/brands', brandData),
  getBrands: () => apiClient.get('/admin/brands'),
  getBrand: (id) => apiClient.get(`/admin/brands/${id}`),
  updateBrand: (id, brandData) => apiClient.put(`/admin/brands/${id}`, brandData),
  updateBrandStatus: (id, status) => apiClient.put(`/admin/brands/${id}/status`, { status }),
  deleteBrand: (id) => {
    // console.log('[API] Deleting brand:', id);
    if (!id) {
      throw new Error('Brand ID is required for deletion');
    }
    return apiClient.delete(`/admin/brands/${id}`);
  },
  
  // Brand Dashboard APIs
  getBrandDashboard: () => apiClient.get('/brand/dashboard'),
  getBrandChats: () => apiClient.get('/brand/chats'),
  getBrandMessages: (phoneNumber) => apiClient.get(`/brand/messages/${phoneNumber}`),
  sendBrandMessage: (phoneNumber, message) => apiClient.post('/brand/messages', { phoneNumber, message }),
  
  // Business Logic APIs
  getContacts: () => apiClient.get('/brand/contacts'),
  getMessages: (phoneNumber) => apiClient.get(`/brand/messages/${phoneNumber}`),
  sendMessage: (phoneNumber, message) => apiClient.post('/brand/messages', { phoneNumber, message }),
  getWelcomeMessage: () => apiClient.get('/brand/welcome'),
  sendWelcomeMessage: (phoneNumber) => apiClient.post('/brand/welcome/send', { phoneNumber }),
  selectOption: (phoneNumber, option) => apiClient.post('/brand/option', { phoneNumber, option }),
  sendButtonResponse: (phoneNumber, buttonId, buttonText) =>
    apiClient.post('/brand/button-response', { phoneNumber, buttonId, buttonText }),
  handlePickupRequest: (data) => apiClient.post('/brand/pickup-request', data),
  getOrderStatus: (storeCode, mobile, invoiceNo) =>
    apiClient.get(`/brand/order-status?storeCode=${storeCode}&mobile=${mobile}&invoiceNo=${invoiceNo}`),
  getStores: () => apiClient.get('/brand/stores'),
  getTimeSlots: (storeCode, date) =>
    apiClient.get(`/brand/store/${storeCode}/timeslots${date ? `?date=${date}` : ''}`)
};

export default apiService;
